import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Autocomplete,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { PriceList, PriceListEntry, Product } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { CatalogListToolbar } from '../common/CatalogListToolbar';
import { SortableFilterableHeader, SortDirection } from '../common/SortableFilterableHeader';

// Detail page for one price list's entries (worker brief
// "price-list-management-restructure", 2026-09-05) - carries over every
// feature the old "Gestionar precios" modal (PriceListManagement.tsx, pre-
// restructure) had: the cross-list price-change alert, the post-import
// result summary, the always-checkable "Productos sin precio en esta lista"
// panel (CLAUDE.md §4), add/edit/delete one entry, and Excel import - just
// re-shelled as its own routed page using the shared CatalogListToolbar/
// SortableFilterableHeader components ProductList.tsx already established,
// instead of a Dialog. No per-column filter-with-create-new dropdowns here
// (that ask was scoped to Materiales/MO/Fletes columns, not this page).
type SortKey = 'codigo' | 'price' | 'effective_date' | 'descuento';

export const PriceListDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const priceListId = Number(id);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [list, setList] = useState<PriceList | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [entries, setEntries] = useState<PriceListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [crossListAlerts, setCrossListAlerts] = useState<string[]>([]);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; errors: string[]; missing_active_products: string[] } | null>(null);
  const [importing, setImporting] = useState(false);

  // "Productos sin precio en esta lista" (CLAUDE.md §4) - checkable en
  // cualquier momento, no solo justo después de una importación.
  const [missingProducts, setMissingProducts] = useState<Array<{ id: number; codigo: string; descripcion?: string }>>([]);
  const [missingProductsLoading, setMissingProductsLoading] = useState(false);

  // Búsqueda (misma posición/comportamiento que las otras páginas de
  // catálogo vía CatalogListToolbar) - filtra la tabla de entradas por
  // código/descripción del producto.
  const [searchTerm, setSearchTerm] = useState('');

  // Orden por columna (sin filtro-dropdown-con-crear-nuevo en esta página,
  // per brief - solo TableSortLabel).
  const [sortKey, setSortKey] = useState<SortKey>('codigo');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // New entry form. CLAUDE.md §4: una entrada es effective_date + descuento
  // (fracción) propios, no un rango.
  const [newEntryProduct, setNewEntryProduct] = useState<Product | null>(null);
  const [newEntryPrice, setNewEntryPrice] = useState<string>('');
  const [newEntryDate, setNewEntryDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [newEntryDescuentoPct, setNewEntryDescuentoPct] = useState<string>('');

  // Edit entry
  const [editEntryDialog, setEditEntryDialog] = useState(false);
  const [editEntry, setEditEntry] = useState<PriceListEntry | null>(null);
  const [editEntryPrice, setEditEntryPrice] = useState<string>('');
  const [editEntryDate, setEditEntryDate] = useState<string>('');
  const [editEntryDescuentoPct, setEditEntryDescuentoPct] = useState<string>('');

  const loadList = useCallback(async () => {
    if (!priceListId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.priceLists.getById(priceListId);
      setList(data);
    } catch (err: any) {
      console.error('Error loading price list:', err);
      setError('Error al cargar la lista de precios');
    } finally {
      setLoading(false);
    }
  }, [priceListId]);

  const loadProducts = useCallback(async () => {
    try {
      const data: any = await apiService.products.getAll({ active_only: true, limit: 1000 });
      setProducts(Array.isArray(data) ? data : data.items || []);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  }, []);

  const refreshEntries = useCallback(async () => {
    if (!priceListId) return;
    try {
      setEntriesLoading(true);
      const data = await apiService.priceLists.getEntries(priceListId);
      setEntries(data);
    } catch (err) {
      console.error('Error loading entries:', err);
      setError('Error al cargar las entradas de precio');
    } finally {
      setEntriesLoading(false);
    }
  }, [priceListId]);

  const refreshMissingProducts = useCallback(async () => {
    if (!priceListId) return;
    try {
      setMissingProductsLoading(true);
      const data = await apiService.priceLists.getMissingProducts(priceListId);
      setMissingProducts(data);
    } catch (err) {
      console.error('Error loading missing products:', err);
    } finally {
      setMissingProductsLoading(false);
    }
  }, [priceListId]);

  useEffect(() => {
    loadList();
    loadProducts();
    refreshEntries();
    refreshMissingProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceListId]);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const handleAddEntry = async () => {
    if (!newEntryProduct || !newEntryPrice || !newEntryDate) return;
    try {
      setError(null);
      const result = await apiService.priceLists.createEntry(priceListId, {
        product_id: newEntryProduct.id,
        price: parseFloat(newEntryPrice),
        effective_date: newEntryDate,
        ...(newEntryDescuentoPct ? { descuento: parseFloat(newEntryDescuentoPct) / 100 } : {}),
      });
      setCrossListAlerts(result.cross_list_alerts || []);
      setNewEntryProduct(null);
      setNewEntryPrice('');
      setNewEntryDescuentoPct('');
      await refreshEntries();
      await refreshMissingProducts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al agregar la entrada de precio');
    }
  };

  const openEditEntry = (entry: PriceListEntry) => {
    setEditEntry(entry);
    setEditEntryPrice(String(entry.price));
    setEditEntryDate(entry.effective_date);
    setEditEntryDescuentoPct(entry.descuento ? String(Number(entry.descuento) * 100) : '');
    setEditEntryDialog(true);
  };

  const handleUpdateEntry = async () => {
    if (!editEntry) return;
    try {
      setError(null);
      const result = await apiService.priceLists.updateEntry(priceListId, editEntry.id, {
        price: parseFloat(editEntryPrice),
        effective_date: editEntryDate,
        descuento: editEntryDescuentoPct ? parseFloat(editEntryDescuentoPct) / 100 : undefined,
      });
      setCrossListAlerts(result.cross_list_alerts || []);
      setEditEntryDialog(false);
      setEditEntry(null);
      await refreshEntries();
      await refreshMissingProducts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al actualizar la entrada de precio');
    }
  };

  const handleDeleteEntry = async (entry: PriceListEntry) => {
    if (!window.confirm('¿Eliminar esta entrada de precio?')) return;
    try {
      await apiService.priceLists.deleteEntry(priceListId, entry.id);
      await refreshEntries();
      await refreshMissingProducts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al eliminar la entrada de precio');
    }
  };

  const handleImportExcel = async (file: File) => {
    try {
      setImporting(true);
      setError(null);
      setImportResult(null);
      const result = await apiService.priceLists.importExcel(priceListId, file);
      setImportResult(result);
      setCrossListAlerts(result.cross_list_alerts || []);
      await refreshEntries();
      await refreshMissingProducts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al importar el archivo Excel');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.priceLists.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_lista_precios.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading template:', err);
      setError('Error al descargar la plantilla');
    }
  };

  const productLabel = (product?: Product) => {
    if (!product) return '—';
    return `${product.codigo}${product.descripcion ? ` - ${product.descripcion}` : ''}`;
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const visibleEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let rows = entries;
    if (term) {
      rows = rows.filter((entry) =>
        entry.product?.codigo?.toLowerCase().includes(term) ||
        entry.product?.descripcion?.toLowerCase().includes(term)
      );
    }
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'codigo':
          cmp = (a.product?.codigo || '').localeCompare(b.product?.codigo || '', undefined, { numeric: true });
          break;
        case 'price':
          cmp = Number(a.price) - Number(b.price);
          break;
        case 'effective_date':
          cmp = a.effective_date.localeCompare(b.effective_date);
          break;
        case 'descuento':
          cmp = Number(a.descuento || 0) - Number(b.descuento || 0);
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [entries, searchTerm, sortKey, sortDirection]);

  if (currentUser?.role !== 'admin') {
    return (
      <Box p={3}>
        <Alert severity="error">
          No tenés permisos para acceder a esta sección. Solo los administradores pueden gestionar listas de precios.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" variant="body2" onClick={() => navigate('/admin/price-lists')}>
          Listas de Precios
        </Link>
        <Typography variant="body2" color="text.primary">{list?.name || '...'}</Typography>
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {crossListAlerts.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setCrossListAlerts([])}>
          {crossListAlerts.map((alert, i) => (
            <Typography key={i} variant="body2">{alert}</Typography>
          ))}
        </Alert>
      )}
      {importResult && (
        <Alert
          severity={importResult.errors.length > 0 ? 'warning' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setImportResult(null)}
        >
          <Typography variant="body2">
            Importación: {importResult.created} creadas, {importResult.updated} actualizadas
            {importResult.errors.length > 0 && `, ${importResult.errors.length} con error`}.
          </Typography>
          {importResult.errors.slice(0, 5).map((e, i) => (
            <Typography key={i} variant="caption" color="text.secondary" display="block">{e}</Typography>
          ))}
        </Alert>
      )}

      {/* "Productos sin precio en esta lista" (CLAUDE.md §4) - checkable en
          cualquier momento, no solo después de una importación. */}
      <Alert severity={missingProducts.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
        {missingProductsLoading ? (
          'Verificando cobertura de precios...'
        ) : missingProducts.length === 0 ? (
          'Todos los productos activos tienen precio en esta lista.'
        ) : (
          <>
            <Typography variant="body2">
              {missingProducts.length} producto(s) activo(s) sin precio en esta lista:
            </Typography>
            <Box sx={{ maxHeight: 120, overflow: 'auto', mt: 0.5 }}>
              {missingProducts.map((p) => (
                <Typography key={p.id} variant="caption" display="block">
                  {p.codigo}{p.descripcion ? ` - ${p.descripcion}` : ''}
                </Typography>
              ))}
            </Box>
          </>
        )}
      </Alert>

      <CatalogListToolbar
        title={list?.name || 'Lista de Precios'}
        subtitle={loading ? undefined : `${entries.length} producto(s) con precio en esta lista`}
        itemNoun="Lista de Precios"
        isAdmin
        onImportClick={() => document.getElementById('price-list-detail-import-input')?.click()}
        onDownloadTemplate={handleDownloadTemplate}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchLoading={importing}
        searchPlaceholder="Buscar por código o descripción..."
      />
      <input
        id="price-list-detail-import-input"
        type="file"
        hidden
        accept=".xlsx,.xls,.csv"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImportExcel(file);
          e.target.value = '';
        }}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Agregar precio</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="flex-start">
            <Autocomplete
              options={products}
              getOptionLabel={(p) => `${p.codigo}${p.descripcion ? ` - ${p.descripcion}` : ''}`}
              value={newEntryProduct}
              onChange={(_, v) => setNewEntryProduct(v)}
              sx={{ minWidth: 260 }}
              renderInput={(params) => <TextField {...params} label="Producto" size="small" />}
            />
            <TextField
              label="Precio (USD)"
              type="number"
              size="small"
              value={newEntryPrice}
              onChange={(e) => setNewEntryPrice(e.target.value)}
              sx={{ width: 130 }}
            />
            <TextField
              label="Vigente desde"
              type="date"
              size="small"
              value={newEntryDate}
              onChange={(e) => setNewEntryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            <TextField
              label="Descuento (%)"
              type="number"
              size="small"
              value={newEntryDescuentoPct}
              onChange={(e) => setNewEntryDescuentoPct(e.target.value)}
              inputProps={{ min: 0, max: 99, step: 0.01 }}
              sx={{ width: 130 }}
            />
            <Button
              variant="contained"
              onClick={handleAddEntry}
              disabled={!newEntryProduct || !newEntryPrice || !newEntryDate}
              sx={{ height: 40 }}
            >
              Agregar
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Divider sx={{ mb: 2 }} />

      {entriesLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'rgb(66,154,45)' }}>
              <TableRow>
                <SortableFilterableHeader
                  label="Producto"
                  align="left"
                  onSort={() => handleSort('codigo')}
                  sortActive={sortKey === 'codigo'}
                  sortDirection={sortDirection}
                />
                <SortableFilterableHeader
                  label="Precio"
                  align="right"
                  onSort={() => handleSort('price')}
                  sortActive={sortKey === 'price'}
                  sortDirection={sortDirection}
                />
                <SortableFilterableHeader
                  label="Vigente desde"
                  align="left"
                  onSort={() => handleSort('effective_date')}
                  sortActive={sortKey === 'effective_date'}
                  sortDirection={sortDirection}
                />
                <SortableFilterableHeader
                  label="Descuento"
                  align="right"
                  onSort={() => handleSort('descuento')}
                  sortActive={sortKey === 'descuento'}
                  sortDirection={sortDirection}
                />
                <SortableFilterableHeader label="Acciones" align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{productLabel(entry.product)}</TableCell>
                  <TableCell align="right">US$ {Number(entry.price).toFixed(2)}</TableCell>
                  <TableCell>{entry.effective_date}</TableCell>
                  {/* Phase 8.7 Round A, item 10: sin decimales - el valor
                      guardado es una fracción Numeric(5,4) (CLAUDE.md §4),
                      se muestra como entero (10% en vez de 10.00%). */}
                  <TableCell align="right">{entry.descuento ? `${Math.round(Number(entry.descuento) * 100)}%` : '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEditEntry(entry)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteEntry(entry)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {visibleEntries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'Ningún precio coincide con la búsqueda.' : 'Esta lista todavía no tiene precios cargados'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit entry dialog - queda como diálogo chico (edita UNA entrada a
          la vez), a diferencia del viejo modal grande que gestionaba toda
          la lista - ese es justamente el que esta página reemplaza. */}
      <Dialog open={editEntryDialog} onClose={() => setEditEntryDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar Precio - {productLabel(editEntry?.product)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Precio (USD)"
              type="number"
              value={editEntryPrice}
              onChange={(e) => setEditEntryPrice(e.target.value)}
              fullWidth
            />
            <TextField
              label="Vigente desde"
              type="date"
              value={editEntryDate}
              onChange={(e) => setEditEntryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Descuento (%)"
              type="number"
              value={editEntryDescuentoPct}
              onChange={(e) => setEditEntryDescuentoPct(e.target.value)}
              inputProps={{ min: 0, max: 99, step: 0.01 }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditEntryDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleUpdateEntry}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default PriceListDetail;
